import {
  Animatic,
  AnimaticPrimitiveConfig,
  ConditionExpression,
  LabelConfig,
  LabelState,
  Namespace,
  Ref,
} from '@repo/sdk/models/components';
import { DefrostScheduleConfig, RelatedDeviceSection } from '@repo/sdk/models/components';

import { dedupeRefs, extractRefsFromConditionExpression, isRef } from '@/helpers/extract-refs';

/**
 * Extracts all Ref objects from a SpecificAnimaticConfig
 * Handles both individual Ref properties (e.g., AnimaticControlValveConfig.valueRef)
 * and Ref arrays (e.g., AnimaticCondenserConfig.fanOnRefs)
 * @param specificAnimaticConfig - The specific animatic configuration object
 * @returns Array of Ref objects found in the configuration
 */
export const extractSpecificAnimaticConfigRefs = (
  specificAnimaticConfig: Record<string, any>
): Ref[] => {
  const refs: Ref[] = [];

  // Iterate through all device-specific configs (e.g., condenser, controlValve, etc.)
  Object.values(specificAnimaticConfig).forEach((configValue: any) => {
    if (configValue && typeof configValue === 'object') {
      // Check all properties of the config object for Refs or Ref arrays
      Object.values(configValue).forEach((propertyValue: any) => {
        // Case 1: Single Ref object (e.g., AnimaticControlValveConfig.valueRef)
        if (isRef(propertyValue)) {
          refs.push(propertyValue);
        }
        // Case 2: Array of Refs (e.g., AnimaticCondenserConfig.fanOnRefs)
        else if (Array.isArray(propertyValue) && propertyValue.length > 0) {
          propertyValue.forEach((item: Ref) => {
            if (isRef(item)) {
              refs.push(item);
            }
          });
        }
      });
    }
  });

  return refs;
};

/**
 * Extracts refs from an animatic configuration object
 * @param animatic - The animatic configuration object
 * @param isSetting - If true, extract only Setting refs; if false, extract non-Setting refs
 * @returns Array of Ref objects
 */
export const extractAnimaticRefs = (animatic: Animatic | undefined, isSetting: boolean): Ref[] => {
  if (!animatic) return [];

  const refs: Ref[] = [];
  // Pull refs from the animatic primitive states condition expressions
  animatic.primitive.states.forEach(
    ({
      conditionExpression,
      config,
    }: {
      conditionExpression?: ConditionExpression | undefined;
      config?: AnimaticPrimitiveConfig | undefined;
    }) => {
      refs.push(...extractRefsFromConditionExpression(conditionExpression, isSetting));

      if (config?.specificAnimaticConfig) {
        refs.push(...extractSpecificAnimaticConfigRefs(config.specificAnimaticConfig));
      }
    }
  );
  // Pull out the refs from the animatic labels
  animatic.labels?.forEach(({ states }: { states: LabelState[] }) => {
    states.forEach(({ config }: { config: LabelConfig }) => {
      if (config?.ref) {
        const ref = config.ref;
        if (
          ref &&
          (isSetting ? ref.namespace === Namespace.Setting : ref.namespace !== Namespace.Setting)
        ) {
          refs.push(ref);
        }
      }
      if (config.refSum) {
        config.refSum.forEach(({ ref }: { ref: Ref }) => {
          if (
            ref &&
            (isSetting ? ref.namespace === Namespace.Setting : ref.namespace !== Namespace.Setting)
          ) {
            refs.push(ref);
          }
        });
      }
    });
  });

  // Pull refs from the animatic shelf (icon shelf and value shelf)
  if (animatic.shelf) {
    // Extract refs from icon shelf
    if (animatic.shelf.iconShelf) {
      animatic.shelf.iconShelf.icons.forEach((icon) => {
        icon.states.forEach((state) => {
          refs.push(...extractRefsFromConditionExpression(state.conditionExpression, isSetting));
        });
      });
    }

    // Extract refs from value shelf
    if (animatic.shelf.valueShelf) {
      animatic.shelf.valueShelf.items.forEach((item) => {
        item.states.forEach((state) => {
          refs.push(...extractRefsFromConditionExpression(state.conditionExpression, isSetting));

          // Extract ref from the config
          if (state.config?.ref) {
            const ref = state.config.ref;
            if (
              ref &&
              (isSetting
                ? ref.namespace === Namespace.Setting
                : ref.namespace !== Namespace.Setting)
            ) {
              refs.push(ref);
            }
          }
        });
      });
    }
  }

  return dedupeRefs(refs.filter((ref) => ref.id));
};

/**
 * extractDefrostScheduleRefs
 * Extracts the defrost schedule refs from the defrost schedule configuration
 *
 * @param defrostScheduleConfig - The defrost schedule configuration to extract the refs from
 * @param isSetting - If true, extract only Setting refs; if false, extract non-Setting refs
 * @returns Array of Ref objects
 */
export const extractDefrostScheduleRefs = (
  defrostScheduleConfig: DefrostScheduleConfig | undefined,
  isSetting: boolean
): Ref[] => {
  if (!defrostScheduleConfig) return [];
  return Object.values(defrostScheduleConfig).filter(
    (ref): ref is Ref =>
      ref !== undefined &&
      (isSetting ? ref.namespace === Namespace.Setting : ref.namespace !== Namespace.Setting)
  );
};

/**
 * extractRelatedDeviceRefs
 * Extracts the related device refs from the related device sections
 *
 * @param relatedDevices - The related device sections to extract the refs from
 * @param isSetting - If true, extract only Setting refs; if false, extract non-Setting refs
 * @returns Array of Ref objects
 */
export const extractRelatedDeviceRefs = (
  relatedDevices: RelatedDeviceSection[] | undefined,
  isSetting: boolean
): Ref[] => {
  if (!relatedDevices) return [];
  const refs = relatedDevices.flatMap((section) => [
    ...section.devices.flatMap((device) => [
      // Get the setting refs from the device attributes
      ...device.attributes.map(({ ref }) => ref),
      // Get the setting refs from the device icon states
      ...device.icon.states.map(({ conditionExpression }) => conditionExpression?.condition?.ref),
    ]),
  ]);

  return (
    refs.filter(
      (ref): ref is Ref =>
        ref !== undefined &&
        (isSetting ? ref.namespace === Namespace.Setting : ref.namespace !== Namespace.Setting)
    ) ?? []
  );
};
